import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";
import { ChevronLeft, Mail, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function DeleteAccount() {
  const [copied, setCopied] = useState(false);
  const supportEmail = "brajeshc56@gmail.com";
  const subject = encodeURIComponent("Delete my BrimiH&G account");
  const body = encodeURIComponent(
    "Hello BrimiH&G team,\n\nPlease delete my account and all associated study data.\n\n" +
      "Account email: <your-registered-email-here>\n\nThank you."
  );
  const mailto = `mailto:${supportEmail}?subject=${subject}&body=${body}`;

  const copyEmail = () => {
    navigator.clipboard?.writeText(supportEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-brimi-cream">
      <Seo
        title="Delete your account — BrimiH&G"
        description="How to permanently delete your BrimiH&G account and all study data."
        path="/delete-account"
      />
      <div className="max-w-2xl mx-auto px-5 pt-8 pb-24 md:pb-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-brimi-navyText/70 hover:text-brimi-navy" data-testid="back-home">
          <ChevronLeft size={16} /> Home
        </Link>

        <h1 className="text-4xl font-heading font-bold text-brimi-navyText mt-4">Delete your account</h1>
        <p className="text-brimi-navyText/70 mt-2">
          You can permanently delete your BrimiH&amp;G account and all study data at any time. There&apos;s no charge,
          and we&apos;ll confirm by email once it&apos;s done.
        </p>

        <div className="brimi-card p-6 mt-8 space-y-5">
          <h2 className="font-heading font-semibold text-xl text-brimi-navyText">How to request deletion</h2>
          <ol className="space-y-4 text-brimi-navyText/85">
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 grid place-items-center rounded-full bg-brimi-navy text-white text-sm font-bold flex-shrink-0 mt-0.5">
                1
              </span>
              <div>
                <p className="font-medium text-brimi-navyText">Email us from your registered email address</p>
                <p className="text-sm text-brimi-navyText/70 mt-1">
                  Send a short message asking for deletion. Use the same email you signed up with so we can verify
                  the request.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-7 h-7 grid place-items-center rounded-full bg-brimi-navy text-white text-sm font-bold flex-shrink-0 mt-0.5">
                2
              </span>
              <div>
                <p className="font-medium text-brimi-navyText">We confirm and delete within 7 days</p>
                <p className="text-sm text-brimi-navyText/70 mt-1">
                  We&apos;ll reply within 48 hours to confirm, and complete the deletion within 7 days.
                </p>
              </div>
            </li>
          </ol>

          <div className="flex flex-col sm:flex-row gap-3 pt-3">
            <a href={mailto} className="brimi-btn-primary justify-center" data-testid="email-delete-btn">
              <Mail size={16} /> Email deletion request
            </a>
            <button
              onClick={copyEmail}
              className="brimi-btn-ghost border border-brimi-creamSoft justify-center"
              data-testid="copy-email-btn"
            >
              {copied ? <CheckCircle2 size={16} className="text-brimi-sage" /> : <Mail size={16} />}
              {copied ? "Copied!" : supportEmail}
            </button>
          </div>
        </div>

        <div className="brimi-card p-6 mt-5 border-brimi-terracotta/30 bg-brimi-terracotta/5">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-brimi-terracotta flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-heading font-semibold text-brimi-navyText">What gets deleted</p>
              <ul className="mt-2 text-sm text-brimi-navyText/80 space-y-1">
                <li>• Your account (name, email, password hash)</li>
                <li>• All chapter progress, MCQ history, mock test scores</li>
                <li>• Map work progress</li>
                <li>• AI tutor chat history</li>
                <li>• Payment records (kept for 7 years for legal/accounting reasons in summarised form, with no personal identifiers)</li>
              </ul>
              <p className="text-sm text-brimi-navyText/70 mt-3">
                Deletion is permanent. You won&apos;t be able to recover any data afterwards.
              </p>
            </div>
          </div>
        </div>

        <p className="text-sm text-brimi-navyText/60 mt-6">
          Questions? See our{" "}
          <Link to="/privacy" className="text-brimi-navy font-semibold">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
